import { useEffect, useRef, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabaseGet } from "@/utils/supabaseRest";

const POLL_INTERVAL = 30_000; // 30 seconds

interface SessionEvent {
  vertical_id: string;
  revenue: number;
  created_at: string;
}

const ConversionToast = () => {
  const { toast } = useToast();
  const lastCheckRef = useRef<string>(new Date().toISOString());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkConversions = useCallback(async () => {
    // Only poll when tab is visible
    if (document.visibilityState !== "visible") return;

    try {
      const since = lastCheckRef.current;
      const events = await supabaseGet<SessionEvent[]>(
        `session_events?select=vertical_id,revenue,created_at&event_type=eq.converted&created_at=gte.${encodeURIComponent(since)}&order=created_at.desc&limit=5`
      );

      if (events && events.length > 0) {
        for (const event of events) {
          const revenue = typeof event.revenue === "number" ? `$${event.revenue.toFixed(2)}` : "$0.00";
          const verticalName = event.vertical_id || "Unknown";

          toast({
            title: "New conversion!",
            description: `${verticalName} — ${revenue}`,
            variant: "default",
          });
        }

        // Update lastCheck to the most recent event's created_at
        lastCheckRef.current = events[0].created_at;
      }
    } catch {
      // Silently ignore polling errors to avoid spamming the console
    }
  }, [toast]);

  useEffect(() => {
    // Start polling
    intervalRef.current = setInterval(checkConversions, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkConversions]);

  // This component renders nothing visible
  return null;
};

export default ConversionToast;
